import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, X, Zap, Play, FileText, Loader2, Upload, Mail, Phone,
  CheckCircle, BarChart3, Download, TableProperties, Search,
  Sparkles, ExternalLink, MapPin, Briefcase, RefreshCw, Trash2,
  ChevronUp, ChevronDown, Filter, User, SlidersHorizontal, Github,
  Linkedin, Star, Database, AlertCircle, Calendar
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import api from '@/lib/api';
import ScoreDetailModal from './ScoreDetailModal';
import ScheduleInterviewModal from '../interviews/ScheduleInterviewModal';
import ScoreCard from '../ScoreCard';
import V4ScoreReportModal from './V4ScoreReportModal';
import { ScoreBadge } from '../ScoreBadge';
import EnrichContactBadge from '../EnrichContactBadge';
import { CandidateFilters } from './CandidateFilters';
import { CandidateTable } from './CandidateTable';
import CandidateKanbanBoard from './CandidateKanbanBoard';
import SubmitCandidateModal from '../submissions/SubmitCandidateModal';

// Platform options for picker
const PLATFORMS = [
  { value: 'both',    label: 'Both Platforms',  sub: 'GitHub + Exa AI',           bg: 'bg-[#2563eb]', Icon: Sparkles },
  { value: 'github',  label: 'GitHub Only',      sub: 'Developers by code',          bg: 'bg-gray-900',  Icon: Github },
  { value: 'linkedin',label: 'LinkedIn Only',    sub: 'Exa AI Neural Search',     bg: 'bg-[#0A66C2]', Icon: () => <span className="text-xs font-bold">in</span> },
];

// Fixed-position platform picker to avoid overflow clipping
function PlatformPicker({ anchorEl, onSelect, onClose }) {
  const ref = useRef(null);
  const [selected, setSelected] = useState('both');
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const w = 260;
      let left = rect.left;
      if (left + w > window.innerWidth - 16) left = window.innerWidth - w - 16;
      setPos({ top: rect.bottom + 6, left });
    }
  }, [anchorEl]);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !anchorEl?.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [anchorEl, onClose]);

  if (!anchorEl) return null;

  return (
    <div ref={ref} style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, width: 260 }}
      className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4" data-testid="platform-picker">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Choose Sourcing Platform</p>
      <div className="space-y-2 mb-4">
        {PLATFORMS.map(p => {
          const isSel = selected === p.value;
          return (
            <button key={p.value} onClick={() => setSelected(p.value)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${isSel ? 'border-[#2563eb] bg-blue-50' : 'border-transparent hover:bg-slate-50'}`}
              data-testid={`platform-option-${p.value}`}>
              <div className={`w-8 h-8 rounded-xl ${p.bg} flex items-center justify-center flex-shrink-0`}>
                <p.Icon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isSel ? 'text-[#2563eb]' : 'text-slate-700'}`}>{p.label}</p>
                <p className="text-[10px] text-slate-400">{p.sub}</p>
              </div>
              {isSel && <CheckCircle className="w-4 h-4 text-[#2563eb] flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      <button onClick={() => { onSelect(selected); onClose(); }}
        className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
        data-testid="confirm-platform-btn">
        <Sparkles className="w-4 h-4" />
        Source on {selected === 'both' ? 'Both Platforms' : selected === 'github' ? 'GitHub' : 'LinkedIn'}
      </button>
    </div>
  );
}

// ─── CONSTANTS ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:     { bg: 'bg-slate-100',  text: 'text-slate-600',   dot: 'bg-slate-400',   label: 'Pending' },
  scored:      { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'Scored' },
  shortlisted: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Shortlisted' },
  screened:    { bg: 'bg-blue-50',    text: 'text-[#2563eb]',   dot: 'bg-[#2563eb]',   label: 'Screened' },
  rejected:    { bg: 'bg-red-50',     text: 'text-red-600',     dot: 'bg-red-500',     label: 'Rejected' },
};

const SOURCE_ICONS = {
  'Manual': '✏️', 'GitHub': '⌥', 'Stack Overflow': '◆',
  'Dev.to': '◈', 'Reddit': '●', 'Twitter/X': '𝕏',
  'LinkedIn': 'in',
};

const SOURCE_BG = {
  'Manual': 'bg-slate-100 text-slate-600',
  'GitHub': 'bg-gray-900 text-white',
  'Stack Overflow': 'bg-orange-50 text-orange-700',
  'Dev.to': 'bg-slate-800 text-white',
  'Reddit': 'bg-orange-100 text-orange-800',
  'LinkedIn': 'bg-[#0A66C2] text-white',
};
const getSourceStyle = (col) => SOURCE_BG[col] || 'bg-blue-50 text-[#2563eb]';

const scoreColor = (s) => !s && s !== 0 ? 'text-slate-300' : s >= 85 ? 'text-emerald-600' : s >= 70 ? 'text-[#2563eb]' : s >= 50 ? 'text-amber-600' : 'text-red-500';

const RESUME_ACCEPT = '.pdf,.doc,.docx,.txt';

const SOURCING_MESSAGES = [
  "Searching LinkedIn profiles...",
  "Expanding location cities...",
  "Scoring candidates with AI...",
  "Finalizing results...",
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function CandidatesPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);

  // Multi-select
  const [selected, setSelected] = useState(new Set()); // Set of candidate_ids
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Single delete
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(null); // candidate to schedule

  // Sort
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Column filters (per-column filter state)
  const [colFilters, setColFilters] = useState({
    name: '',
    source: 'all',
    status: 'all',
    score: 'all',
    relevance: 'all',
    location: '',
  });

  // Global search
  const [globalSearch, setGlobalSearch] = useState('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(null);
  const [v4ScoreModal, setV4ScoreModal]     = useState(null); // {candidate, source}
  const [showLegacyMenu, setShowLegacyMenu] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [showSourcingModal, setShowSourcingModal] = useState(false);

  // Form
  const [resumeMode, setResumeMode] = useState('text');
  const [form, setForm] = useState({ name: '', email: '', phone: '', resume_text: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scoringId, setScoringId] = useState(null);
  const [bulkScoring, setBulkScoring] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  // Sourcing
  const [sourcingSession, setSourcingSession] = useState(null);
  const [sourcingLoading, setSourcingLoading] = useState(false);
  const [sourcingActive, setSourcingActive] = useState(false);
  const [sourcingStatus, setSourcingStatus] = useState("");
  const [showPlatformPicker, setShowPlatformPicker] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  // View mode: 'list' | 'board'
  const [viewMode, setViewMode] = useState('list');
  // Top-level tab: 'candidates' | 'sourcing'
  const [activeTab, setActiveTab] = useState('candidates');
  const [submitModalCandidate, setSubmitModalCandidate] = useState(null);
  const [pipelineCounts, setPipelineCounts] = useState({});
  // Global glass toast
  const { addToast } = useToast();
  // Enrichment state
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [showScorePopover, setShowScorePopover] = useState(null);
  const enrichPollingRef = useRef(null);
  const pollingRef = useRef(null);
  const fileRef = useRef(null);
  const csvRef = useRef(null);

  // ─── DATA FETCH ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const queries = [api.get('/candidates' + (jobId ? `?job_id=${jobId}` : '')), api.get('/agents')];
      if (jobId) queries.unshift(api.get(`/jobs/${jobId}`));
      const results = await Promise.all(queries);
      if (jobId) { setJob(results[0].data); setCandidates(results[1].data || []); setAgents(results[2].data || []); }
      else { setCandidates(results[0].data || []); setAgents(results[1].data || []); }
      setSelected(new Set()); // clear selection on refresh
    } catch (err) { logger.error(err); }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (jobId) checkSourcingStatus(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch pipeline submission counts for live kanban column header badges
  useEffect(() => {
    if (!jobId) return;
    api.get(`/submissions/pipeline-counts?job_id=${jobId}`)
      .then(r => setPipelineCounts(r.data))
      .catch(() => {});
  }, [jobId]);

  // On load: check if enrichment was already running or completed
  useEffect(() => {
    if (jobId) {
      api.get(`/enrichment/${jobId}/status`).then(({ data }) => {
        if (data.status && data.status !== 'not_started') {
          setEnrichmentStatus(data);
          if (data.status === 'pre_filtering' || data.status === 'scraping') {
            // Resume polling if enrichment was in progress
            if (!enrichPollingRef.current) {
              enrichPollingRef.current = setInterval(async () => {
                try {
                  const { data: s } = await api.get(`/enrichment/${jobId}/status`);
                  setEnrichmentStatus(s);
                  if (s.status === 'completed' || s.status === 'error') {
                    clearInterval(enrichPollingRef.current);
                    enrichPollingRef.current = null;
                    if (s.status === 'completed') {
                      addToast(`✅ Enrichment done! ${s.enriched} profiles enriched.`, 'success');
                      fetchData();
                    }
                  }
                } catch { clearInterval(enrichPollingRef.current); enrichPollingRef.current = null; }
              }, 5000);
            }
          }
        }
      }).catch(() => {});
    }
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ── Sourcing progress messages: cycle every 10s while pipeline is running ──
  useEffect(() => {
    if (!sourcingActive) { setSourcingStatus(""); return; }
    setSourcingStatus(SOURCING_MESSAGES[0]);
    let i = 1;
    const interval = setInterval(() => {
      setSourcingStatus(SOURCING_MESSAGES[i % SOURCING_MESSAGES.length]);
      i++;
    }, 10000);
    return () => clearInterval(interval);
  }, [sourcingActive]);

  // checkSourcingStatus — v4 only
  const checkSourcingStatus = async () => {
    try {
      const { data: v4 } = await api.get(`/sourcing/v4/${jobId}/latest`);
      if (v4 && ['started','generating_query','searching','gating','scoring','building_profiles'].includes(v4.status)) {
        setSourcingSession({ ...v4, _pipeline: 'v4' });
        setSourcingActive(true);
        startV4Polling(v4.session_id);
      }
    } catch {}
  };

  // v4 polling — uses session_id
  const startV4Polling = (sessionId) => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/sourcing/v4/${jobId}/status/${sessionId}`);
        setSourcingSession({ ...data, _pipeline: 'v4' });
        if (['completed', 'error'].includes(data.status)) {
          clearInterval(pollingRef.current); pollingRef.current = null;
          setSourcingActive(false);
          if (data.status === 'completed') {
            if ((data.candidates_saved || 0) > 0) {
              addToast(`Sourcing finished — ${data.candidates_saved} candidates added.`, 'success');
            } else {
              addToast('Sourcing finished. No new candidates matched this run.', 'info');
            }
            fetchData();
          } else {
            addToast(data.error || 'AI sourcing failed.', 'error');
          }
        }
      } catch { clearInterval(pollingRef.current); pollingRef.current = null; }
    }, 5000);
  };

  // ── Legacy sourcing disabled — v4 is the only pipeline ──────────────────────
  const startSourcing = async () => {
    addToast('Use the Source button to start AI sourcing (v4 pipeline).', 'info');
  };

  // Legacy polling — job-level status (no-op, kept to avoid ref errors)
  const startPolling = () => {};


  const startEnrichment = async () => {
    if (!jobId) return;
    setEnrichmentLoading(true);
    try {
      const { data } = await api.post(`/enrichment/${jobId}/start`);
      setEnrichmentStatus(data);
      if (!data.already_running) {
        addToast(`Enriching ${data.linkedin_candidates} LinkedIn profiles via BrightData (~${data.cost_estimate})`, 'success');
        // Start polling enrichment status every 5s (was 10s)
        if (enrichPollingRef.current) clearInterval(enrichPollingRef.current);
        enrichPollingRef.current = setInterval(async () => {
          try {
            const { data: s } = await api.get(`/enrichment/${jobId}/status`);
            setEnrichmentStatus(s);
            if (s.status === 'completed' || s.status === 'error') {
              clearInterval(enrichPollingRef.current);
              enrichPollingRef.current = null;
              if (s.status === 'completed') {
                addToast(`✅ Enrichment complete! ${s.enriched} of ${s.total_candidates} profiles enriched${s.rejected_non_us > 0 ? ` (${s.rejected_non_us} non-US rejected)` : ''}`, 'success');
                fetchData();
              } else {
                addToast(`Enrichment failed: ${s.error || 'Unknown error'}`, 'error');
              }
            }
          } catch { clearInterval(enrichPollingRef.current); enrichPollingRef.current = null; }
        }, 5000);  // Poll every 5 seconds
      } else {
        addToast('Enrichment already in progress', 'info');
      }
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to start enrichment', 'error');
    } finally {
      setEnrichmentLoading(false);
    }
  };


  // ── v4 sourcing (DEFAULT) ─────────────────────────────────────────────────
  const startSourcingV4 = async () => {
    if (!jobId) return;
    setSourcingLoading(true);
    setSourcingActive(true);
    try {
      addToast('AI sourcing started. This can take 30–60 seconds.', 'info');
      const { data } = await api.post(`/sourcing/v4/${jobId}/start`, { max_candidates: 20 });
      setSourcingSession({ ...data, _pipeline: 'v4' });
      setShowSourcingModal(true);
      if (data.session_id) startV4Polling(data.session_id);
    } catch (err) {
      addToast(err.response?.data?.detail || 'AI sourcing failed.', 'error');
    } finally {
      setSourcingLoading(false);
    }
  };

  // Auto-start when navigated with ?source= platform param from JobsPage
  useEffect(() => {
    const sourcePlatform = searchParams.get('source');
    if (sourcePlatform && jobId && candidates.length >= 0 && !sourcingSession) {
      setTimeout(() => startSourcingV4(), 1000);
    }
  }, [searchParams.get('source'), jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── SELECTION ───────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.candidate_id)));
  };

  // ─── BULK DELETE ─────────────────────────────────────────────────────────
  const [bulkShortlisting, setBulkShortlisting] = useState(false);
  const [minLiScore, setMinLiScore] = useState(0);

  const bulkShortlist = async () => {
    if (selected.size === 0) return;
    setBulkShortlisting(true);
    try {
      const { data } = await api.post('/candidates/bulk-shortlist', { candidate_ids: [...selected] });
      addToast(`${data.shortlisted} candidate${data.shortlisted !== 1 ? 's' : ''} moved to Shortlisted`, 'success');
      setSelected(new Set());
      fetchData();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Shortlist action failed', 'error');
    } finally {
      setBulkShortlisting(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post('/candidates/bulk-delete', { candidate_ids: [...selected] });
      addToast(`Deleted ${data.deleted} candidate(s)`, 'success');
      setShowBulkDelete(false);
      setSelected(new Set());
      await fetchData();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Bulk delete failed', 'error');
    }
    finally { setBulkDeleting(false); }
  };

  // ─── SINGLE DELETE ───────────────────────────────────────────────────────
  const deleteCandidate = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/candidates/${id}`);
      setDeleteConfirm(null);
      await fetchData();
      addToast('Candidate deleted', 'success');
    } catch { addToast('Delete failed', 'error'); }
    finally { setDeleting(null); }
  };

  // ─── SINGLE CANDIDATE ENRICH ─────────────────────────────────────────────
  const [enrichingId, setEnrichingId] = useState(null);

  const enrichSingleCandidate = async (candidateId) => {
    setEnrichingId(candidateId);
    try {
      // Trigger enrichment for this specific candidate
      const { data } = await api.post(`/enrichment/${jobId}/start`, { candidate_ids: [candidateId] });
      addToast(`Enriching LinkedIn profile via BrightData...`, 'success');
      // Poll for completion
      const checkDone = setInterval(async () => {
        try {
          const cands = await api.get(`/candidates/${candidateId}`);
          if (cands.data?.enrichment_status === 'enriched') {
            clearInterval(checkDone);
            setEnrichingId(null);
            addToast(`Profile enriched! Ready to score.`, 'success');
            fetchData();
          } else if (cands.data?.enrichment_status?.startsWith('rejected')) {
            clearInterval(checkDone);
            setEnrichingId(null);
            addToast('Profile rejected — non-US location detected.', 'error');
            fetchData();
          }
        } catch { clearInterval(checkDone); setEnrichingId(null); }
      }, 8000);
      setTimeout(() => { clearInterval(checkDone); setEnrichingId(null); }, 180000);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Enrich failed', 'error');
      setEnrichingId(null);
    }
  };

  // ─── MULTI-SELECT BULK ENRICH ─────────────────────────────────────────────
  const [bulkEnriching, setBulkEnriching] = useState(false);

  const bulkEnrich = async () => {
    const linkedinSelected = [...selected].filter(id => {
      const c = candidates.find(x => x.candidate_id === id);
      return c?.source_column === 'LinkedIn';
    });
    if (linkedinSelected.length === 0) {
      addToast('No LinkedIn candidates selected. Only LinkedIn profiles can be enriched.', 'error');
      return;
    }
    setBulkEnriching(true);
    try {
      const { data } = await api.post(`/enrichment/${jobId}/start`);
      addToast(`Enriching ${data.linkedin_candidates || linkedinSelected.length} LinkedIn profiles...`, 'success');
      setSelected(new Set());
      // Start polling enrichment status
      if (enrichPollingRef.current) clearInterval(enrichPollingRef.current);
      enrichPollingRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/enrichment/${jobId}/status`);
          setEnrichmentStatus(s);
          if (s.status === 'completed' || s.status === 'error') {
            clearInterval(enrichPollingRef.current);
            enrichPollingRef.current = null;
            setBulkEnriching(false);
            if (s.status === 'completed') {
              addToast(`Enrichment done! ${s.enriched} profiles enriched.`, 'success');
              fetchData();
            }
          }
        } catch { clearInterval(enrichPollingRef.current); enrichPollingRef.current = null; setBulkEnriching(false); }
      }, 10000);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Bulk enrich failed', 'error');
      setBulkEnriching(false);
    }
  };

  // ─── MULTI-SELECT BULK SCORE (parallel) ─────────────────────────────────
  const [bulkScoring2, setBulkScoring2] = useState(false);

  const bulkScoreSelected = async () => {
    const selectedArr = [...selected];
    const scoreable = selectedArr.filter(id => {
      const c = candidates.find(x => x.candidate_id === id);
      return c?.status === 'pending';
    });
    if (scoreable.length === 0) {
      addToast('No pending candidates selected. Score is only for "pending" status candidates.', 'error');
      return;
    }
    setBulkScoring2(true);
    addToast(`Scoring ${scoreable.length} candidates in parallel...`, 'info');
    try {
      // Use the new parallel bulk-score-selected endpoint
      const { data } = await api.post('/candidates/bulk-score-selected', {
        candidate_ids: scoreable
      });
      addToast(`${data.scored} candidates scored, ${data.shortlisted} shortlisted!`, 'success');
      setSelected(new Set());
      await fetchData();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Bulk score failed', 'error');
    }
    finally { setBulkScoring2(false); }
  };



  // ─── FILE UPLOAD ─────────────────────────────────────────────────────────
  const handleResumeUpload = async (file) => {
    if (!file) return;
    setResumeFile(file); setExtracting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/candidates/extract-resume', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, resume_text: data.text })); setResumeMode('text');
      addToast(`Extracted ${data.word_count?.toLocaleString()} words from ${data.format}`, 'success');
    } catch (err) { addToast(err.response?.data?.detail || 'File extraction failed', 'error'); setResumeFile(null); }
    finally { setExtracting(false); }
  };

  // ─── ADD CANDIDATE ───────────────────────────────────────────────────────
  const addCandidate = async (e) => {
    e.preventDefault();
    if (!form.resume_text.trim()) { addToast('Please add resume text or upload a file', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/candidates', { ...form, job_id: jobId });
      setShowAddModal(false); setForm({ name: '', email: '', phone: '', resume_text: '' }); setResumeFile(null);
      await fetchData(); addToast('Candidate added!', 'success');
    } catch (err) { addToast(err.response?.data?.detail || 'Failed to add candidate', 'error'); }
    finally { setSaving(false); }
  };

  // ─── SCORE ───────────────────────────────────────────────────────────────
  const scoreCandidate = async (id) => {
    setScoringId(id);
    try { await api.post(`/candidates/${id}/score`); await fetchData(); addToast('Scored!', 'success'); }
    catch { addToast('Scoring failed', 'error'); }
    finally { setScoringId(null); }
  };

  const bulkScore = async () => {
    setBulkScoring(true);
    try { const { data } = await api.post(`/candidates/bulk-score/${jobId}`); addToast(`${data.scored} scored, ${data.shortlisted} shortlisted!`, 'success'); await fetchData(); }
    catch { addToast('Bulk scoring failed', 'error'); }
    finally { setBulkScoring(false); }
  };

  const startScreening = async (c) => {
    try {
      const { data } = await api.post('/calls', { agent_id: agents[0]?.agent_id || null, candidate_id: c.candidate_id, job_id: jobId, status: 'in_progress' });
      await api.patch(`/candidates/${c.candidate_id}`, { status: 'screened' });
      navigate(`/screening/${data.call_id}`);
    } catch { addToast('Failed to start screening', 'error'); }
  };

  // ─── CSV ─────────────────────────────────────────────────────────────────
  const downloadCsvTemplate = async () => {
    const r = await api.get('/candidates/csv-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a'); a.href = url; a.download = 'template.csv'; a.click(); window.URL.revokeObjectURL(url);
  };

  const importCsv = async () => {
    if (!csvFile || !jobId) return; setCsvImporting(true);
    try {
      const fd = new FormData(); fd.append('file', csvFile);
      const { data } = await api.post(`/candidates/import/${jobId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvResult(data); if (data.imported > 0) { addToast(`Imported ${data.imported}!`, 'success'); await fetchData(); }
    } catch { addToast('Import failed', 'error'); }
    finally { setCsvImporting(false); }
  };

  // ─── SORT ────────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const setColFilter = (key, value) => setColFilters(prev => ({ ...prev, [key]: value }));

  const clearAllFilters = () => {
    setGlobalSearch('');
    setMinLiScore(0);
    setColFilters({ name: '', source: 'all', status: 'all', score: 'all', relevance: 'all', location: '' });
  };

  // ─── DERIVED: unique values for filter dropdowns ──────────────────────────
  const uniqueSources = useMemo(() => {
    const sources = [...new Set(candidates.map(c => c.source_column || (c.source_type === 'sourced' ? 'Sourced' : 'Manual')).filter(Boolean))];
    return sources.map(s => ({ value: s, label: `${SOURCE_ICONS[s] || '◉'} ${s}` }));
  }, [candidates]);

  const uniqueStatuses = useMemo(() => (
    [...new Set(candidates.map(c => c.status).filter(Boolean))].map(s => ({
      value: s,
      label: STATUS_CONFIG[s]?.label || s
    }))
  ), [candidates]);

  // ─── FILTER + SORT ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...candidates];

    // Min LinkedIn score quick-filter
    if (minLiScore > 0) {
      list = list.filter(c => {
        const li = c.linkedinscore || c.linkedin_score || {};
        const s = typeof li.score === 'number' ? li.score :
                  typeof li === 'number' ? li :
                  c.relevancescore ?? c.relevance_score ?? null;
        return typeof s === 'number' && s >= minLiScore;
      });
    }

    // Global search
    if (globalSearch) {
      const s = globalSearch.toLowerCase();
      list = list.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.sourced_data?.current_title?.toLowerCase().includes(s) ||
        c.sourced_data?.current_company?.toLowerCase().includes(s) ||
        c.sourced_data?.skills?.join(' ').toLowerCase().includes(s)
      );
    }

    // Column: Name
    if (colFilters.name) {
      const n = colFilters.name.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(n) || c.email?.toLowerCase().includes(n));
    }

    // Column: Source
    if (colFilters.source && colFilters.source !== 'all') {
      list = list.filter(c => {
        const col = c.source_column || (c.source_type === 'sourced' ? 'Sourced' : 'Manual');
        return col === colFilters.source;
      });
    }

    // Column: Status
    if (colFilters.status && colFilters.status !== 'all') {
      list = list.filter(c => c.status === colFilters.status);
    }

    // Column: Score
    if (colFilters.score && colFilters.score !== 'all') {
      if (colFilters.score === '0') {
        list = list.filter(c => c.score == null && !c.score_details && !c.linkedin_score);
      } else if (colFilters.score === 'high') {
        list = list.filter(c => {
          const s = c.linkedin_score?.score ?? c.score_details?.overall_score ?? (c.score != null ? Math.round(c.score * 10) : null);
          return s != null && s >= 80;
        });
      } else if (colFilters.score === 'medium') {
        list = list.filter(c => {
          const s = c.linkedin_score?.score ?? c.score_details?.overall_score ?? (c.score != null ? Math.round(c.score * 10) : null);
          return s != null && s >= 60 && s < 80;
        });
      } else if (colFilters.score === 'low') {
        list = list.filter(c => {
          const s = c.linkedin_score?.score ?? c.score_details?.overall_score ?? (c.score != null ? Math.round(c.score * 10) : null);
          return s != null && s < 60;
        });
      } else {
        const min = parseInt(colFilters.score);
        if (!isNaN(min)) {
          list = list.filter(c => {
            const s = c.score_details?.overall_score ?? (c.score != null ? c.score * 10 : null);
            return s != null && s >= min;
          });
        }
      }
    }

    // Column: Relevance — use v4 linkedinscore, legacy fallbacks; treat <=0 as unscored
    const getRawLiScore = (c) => {
      const li = c.linkedinscore || c.linkedin_score || {};
      const score = typeof li.score === 'number' ? li.score : null;
      const legacy =
        typeof c.relevancescore === 'number' ? c.relevancescore :
        typeof c.relevance_score === 'number' ? c.relevance_score :
        null;
      const base = score != null ? score : legacy;
      // Treat 0 as unscored for filters/sort as well
      return base != null && base !== 0 ? base : null;
    };

    if (colFilters.relevance && colFilters.relevance !== 'all') {
      if (colFilters.relevance === '0') {
        // Unscored only: no numeric score or score <= 0
        list = list.filter(c => {
          const s = getRawLiScore(c);
          return s == null || s <= 0;
        });
      } else if (colFilters.relevance === 'high') {
        list = list.filter(c => {
          const s = getRawLiScore(c);
          return s != null && s > 0 && s >= 80;
        });
      } else if (colFilters.relevance === 'medium') {
        list = list.filter(c => {
          const s = getRawLiScore(c);
          return s != null && s > 0 && s >= 60 && s < 80;
        });
      } else if (colFilters.relevance === 'low') {
        list = list.filter(c => {
          const s = getRawLiScore(c);
          return s != null && s > 0 && s < 60;
        });
      } else {
        const min = parseInt(colFilters.relevance);
        if (!isNaN(min)) {
          list = list.filter(c => {
            const s = getRawLiScore(c);
            return s != null && s > 0 && s >= min;
          });
        }
      }
    }

    // Column: Location
    if (colFilters.location) {
      const loc = colFilters.location.toLowerCase();
      list = list.filter(c => {
        const candLoc = (c.sourced_data?.us_location || c.us_location || '').toLowerCase();
        return candLoc.includes(loc);
      });
    }

    // Sort
    list.sort((a, b) => {
      let va, vb;
      if (sortField === 'name') { va = a.name?.toLowerCase() || ''; vb = b.name?.toLowerCase() || ''; }
      else if (sortField === 'score') {
        va = a.score_details?.overall_score ?? (a.score ? a.score * 10 : -1);
        vb = b.score_details?.overall_score ?? (b.score ? b.score * 10 : -1);
      }
      else if (sortField === 'relevance') {
        const rawA = (a.linkedinscore && typeof a.linkedinscore.score === 'number' ? a.linkedinscore.score : null)
          ?? (a.linkedin_score && typeof a.linkedin_score.score === 'number' ? a.linkedin_score.score : null)
          ?? (typeof a.relevancescore === 'number' ? a.relevancescore : null)
          ?? (typeof a.relevance_score === 'number' ? a.relevance_score : null);
        const rawB = (b.linkedinscore && typeof b.linkedinscore.score === 'number' ? b.linkedinscore.score : null)
          ?? (b.linkedin_score && typeof b.linkedin_score.score === 'number' ? b.linkedin_score.score : null)
          ?? (typeof b.relevancescore === 'number' ? b.relevancescore : null)
          ?? (typeof b.relevance_score === 'number' ? b.relevance_score : null);
        va = rawA != null && rawA > 0 ? rawA : -1;
        vb = rawB != null && rawB > 0 ? rawB : -1;
      }
      else if (sortField === 'status') { va = a.status || ''; vb = b.status || ''; }
      else if (sortField === 'source') { va = a.source_column || ''; vb = b.source_column || ''; }
      else { va = a.created_at || ''; vb = b.created_at || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [candidates, globalSearch, colFilters, sortField, sortDir, minLiScore]);

  const hasActiveFilters = globalSearch || minLiScore > 0 || Object.values(colFilters).some(v => v && v !== 'all' && v !== '');
  const pendingCount = candidates.filter(c => c.status === 'pending').length;
  const sourcedCount = candidates.filter(c => c.source_type === 'sourced').length;
  const manualCount = candidates.filter(c => c.source_type !== 'sourced').length;
  const linkedinCount = candidates.filter(c => c.source_column === 'LinkedIn').length;
  const enrichedCount = candidates.filter(c => c.enrichment_status === 'enriched').length;
  const isSourceRunning = sourcingSession && [
    'started', 'generating_query', 'searching', 'gating', 'scoring', 'building_profiles', // v4
    'parsing_jd', 'analyzing_tech_stack', 'deep_scanning'  // legacy
  ].includes(sourcingSession?.status);
  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selected.has(c.candidate_id));
  const someSelected = selected.size > 0;

  if (loading) return (
    <div className="flex flex-col h-full bg-slate-50 p-8" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="h-8 bg-slate-100 rounded-lg w-48 mb-6 animate-pulse" />
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* skeleton header */}
        <div className="grid gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200"
          style={{ gridTemplateColumns: '40px 1.8fr 100px 160px 110px 100px 80px 60px 65px 160px' }}>
          {[...Array(10)].map((_, i) => <div key={i} className="h-3 bg-slate-200 rounded animate-pulse" />)}
        </div>
        {/* skeleton rows */}
        {[...Array(5)].map((_, i) => (
          <div key={i} className="grid gap-2 px-4 py-4 border-b border-slate-100 items-center"
            style={{ gridTemplateColumns: '40px 1.8fr 100px 160px 110px 100px 80px 60px 65px 160px' }}>
            <div className="w-4 h-4 bg-slate-100 rounded animate-pulse" />
            <div className="space-y-1.5">
              <div className="h-3 bg-slate-200 rounded w-32 animate-pulse" />
              <div className="h-2.5 bg-slate-100 rounded w-24 animate-pulse" />
            </div>
            {[...Array(8)].map((_, j) => (
              <div key={j} className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${60 + (j * 7) % 40}%` }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="candidates-page" style={{ fontFamily: 'Georgia, serif' }}>

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-8 py-5">
        <div className="flex items-center gap-4">
          {jobId && (
            <button data-testid="back-to-jobs-btn" onClick={() => navigate('/jobs')} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800">{job?.title || 'All Candidates'}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm">
              <span className="text-slate-500">{candidates.length} total</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">{manualCount} manual</span>
              <span className="text-slate-300">·</span>
              <span className="text-[#2563eb] font-semibold">{sourcedCount} sourced</span>
              {job && <><span className="text-slate-300">·</span><span className="text-slate-400">{job.department} · {job.experience_level}</span>
              {job.job_type && <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${job.job_type === 'W2' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{job.job_type}</span>}
              {job.client && <span className="ml-1 text-xs text-slate-400">· {job.client}</span>}
              </>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {jobId && pendingCount > 0 && (
              <button data-testid="bulk-score-btn" onClick={bulkScore} disabled={bulkScoring}
                className="flex items-center gap-2 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-medium px-4 py-2.5 rounded-xl border border-amber-200 disabled:opacity-50">
                {bulkScoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Score All ({pendingCount})
              </button>
            )}
            {jobId && (
              <div className="relative flex items-stretch" data-testid="source-btn-group">
                {/* Main v4 Source button */}
                <button
                  data-testid="source-candidates-btn"
                  onClick={() => { if (isSourceRunning) setShowSourcingModal(true); else startSourcingV4(); }}
                  disabled={sourcingLoading}
                  className={`flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-l-xl border-y border-l shadow-sm transition-all disabled:opacity-50 ${isSourceRunning ? 'bg-blue-50 text-[#2563eb] border-blue-200 animate-pulse' : 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white border-[#2563eb] shadow-blue-500/15'}`}>
                  {sourcingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {isSourceRunning ? 'Sourcing...' : 'Source'}
                </button>
                {/* Legacy dropdown toggle */}
                <button
                  onClick={() => setShowLegacyMenu(m => !m)}
                  disabled={sourcingLoading || isSourceRunning}
                  className={`flex items-center px-2 py-2.5 rounded-r-xl border shadow-sm transition-all disabled:opacity-40 ${isSourceRunning ? 'bg-blue-50 text-[#2563eb] border-blue-200' : 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white border-[#2563eb] border-l border-l-blue-400/40 shadow-blue-500/15'}`}
                  title="More sourcing options"
                  data-testid="source-dropdown-toggle"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {/* Legacy dropdown menu */}
                {showLegacyMenu && !isSourceRunning && (
                  <div className="absolute top-full right-0 mt-1.5 w-52 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 py-1 overflow-hidden"
                       onMouseLeave={() => setShowLegacyMenu(false)}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">More Options</div>
                    <button
                      onClick={() => { setShowLegacyMenu(false); setPickerAnchor(null); startSourcing('linkedin'); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      data-testid="quick-source-btn"
                    >
                      <Database className="w-4 h-4 text-slate-400" />
                      <div className="text-left">
                        <p className="font-medium text-xs">Quick Source</p>
                        <p className="text-[10px] text-slate-400">Legacy pipeline (faster)</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
            {jobId && <button data-testid="import-csv-btn" onClick={() => { setShowCsvModal(true); setCsvResult(null); setCsvFile(null); }} className="p-2.5 bg-white hover:bg-slate-50 text-slate-600 rounded-xl border border-slate-200 transition-colors" title="Import CSV"><TableProperties className="w-4 h-4" /></button>}
            {jobId && <button data-testid="add-candidate-btn" onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2.5 rounded-xl border border-slate-200"><Plus className="w-4 h-4" /> Add Manual</button>}
            {/* ENRICH LINKEDIN PROFILES — BrightData */}
            {jobId && linkedinCount > 0 && (
              <button
                data-testid="enrich-btn"
                onClick={startEnrichment}
                disabled={enrichmentLoading || enrichmentStatus?.status === 'pre_filtering' || enrichmentStatus?.status === 'scraping'}
                className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl border transition-all disabled:opacity-60 shadow-sm ${
                  enrichmentStatus?.status === 'completed'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : enrichmentStatus?.status === 'scraping' || enrichmentStatus?.status === 'pre_filtering'
                    ? 'bg-[#0A66C2]/10 text-[#0A66C2] border-[#0A66C2]/30'
                    : 'bg-white hover:bg-[#0A66C2]/5 text-[#0A66C2] border-[#0A66C2]/30'
                }`}
                title={`Enrich ${linkedinCount} LinkedIn profiles with full work history, education, certs via BrightData (~$${(linkedinCount * 0.0015).toFixed(4)})`}
              >
                {enrichmentLoading || enrichmentStatus?.status === 'scraping' || enrichmentStatus?.status === 'pre_filtering' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {enrichmentStatus?.enriched > 0
                      ? `${enrichmentStatus.enriched}/${enrichmentStatus.total_candidates} Enriched`
                      : 'Enriching...'}
                  </>
                ) : enrichmentStatus?.status === 'completed' ? (
                  <><CheckCircle className="w-4 h-4" /> {enrichmentStatus.enriched} Enriched</>
                ) : (
                  <><Database className="w-4 h-4" /> Enrich LinkedIn ({linkedinCount})</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Global search + filter summary — CandidateFilters */}
        <CandidateFilters
          filters={{ search: globalSearch }}
          onFilterChange={(f) => setGlobalSearch(f.search)}
          onUpload={() => setShowAddModal(true)}
          filtered={filtered.length}
          total={candidates.length}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearAllFilters}
          isSourceRunning={isSourceRunning}
          sourcingStatus={sourcingStatus}
          onSourcingClick={() => setShowSourcingModal(true)}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {/* ── Min LinkedIn Score quick-filter ─────────────────────────── */}
        {candidates.some(c => c.linkedinscore?.score || c.linkedin_score?.score) && (
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="score-threshold-bar">
            <span className="text-xs font-semibold text-slate-500 shrink-0">Min AI Score:</span>
            {[
              { label: 'All',  val: 0  },
              { label: '30+',  val: 30 },
              { label: '40+',  val: 40 },
              { label: '50+',  val: 50 },
              { label: '60+',  val: 60 },
            ].map(({ label, val }) => (
              <button
                key={val}
                data-testid={`score-filter-${val}`}
                onClick={() => setMinLiScore(val)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  minLiScore === val
                    ? 'bg-[#2563eb] text-white border-[#2563eb]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-[#2563eb] hover:text-[#2563eb]'
                }`}
              >
                {label}
              </button>
            ))}
            {minLiScore > 0 && (
              <span className="text-xs text-slate-400 ml-1">
                {filtered.length} of {candidates.length} shown
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── TAB STRIP (job-scoped only) ─────────────────────────────── */}
      {jobId && (
        <div className="flex gap-1 border-b border-slate-200 px-8 bg-white" data-testid="candidates-tab-strip">
          {['candidates','sourcing'].map(tab => (
            <button
              key={tab}
              data-testid={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-[#2563eb] text-[#2563eb]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {tab === 'candidates' ? '👥 Candidates' : '🔍 Sourcing'}
            </button>
          ))}
        </div>
      )}

      {/* ── SOURCING TAB PANEL ─────────────────────────────────────── */}
      {jobId && activeTab === 'sourcing' && (
        <div className="p-8">
          <SourcingPanel jobId={jobId} jobTitle={job?.title} />
        </div>
      )}

      {/* ── CANDIDATES TAB CONTENT (default — wraps existing UI) ───── */}
      {(!jobId || activeTab === 'candidates') && (
      <>

      {/* ── SELECTION ACTION BAR ──────────────────────────────────────── */}
      {someSelected && (
        <div className="bg-[#2563eb] px-8 py-3 flex items-center gap-4" data-testid="selection-bar">
          <span className="text-white text-sm font-semibold">{selected.size} candidate{selected.size !== 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => setSelected(new Set())} className="text-blue-200 hover:text-white text-xs transition-colors">Deselect all</button>
            {/* Enrich LinkedIn Selected */}
            {jobId && (
              <button
                data-testid="bulk-enrich-btn"
                onClick={bulkEnrich}
                disabled={bulkEnriching}
                className="flex items-center gap-2 bg-[#0A66C2] hover:bg-[#0850a0] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
              >
                {bulkEnriching ? <><Loader2 className="w-4 h-4 animate-spin" /> Enriching...</> : <><Database className="w-4 h-4" /> Enrich LinkedIn</>}
              </button>
            )}
            {/* Shortlist Selected */}
            <button
              data-testid="bulk-shortlist-btn"
              onClick={bulkShortlist}
              disabled={bulkShortlisting}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
            >
              {bulkShortlisting ? <><Loader2 className="w-4 h-4 animate-spin" /> Shortlisting...</> : <><CheckCircle className="w-4 h-4" /> Shortlist</>}
            </button>
            {/* Score Selected */}
            <button
              data-testid="bulk-score-selected-btn"
              onClick={bulkScoreSelected}
              disabled={bulkScoring2}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
            >
              {bulkScoring2 ? <><Loader2 className="w-4 h-4 animate-spin" /> Scoring...</> : <><Zap className="w-4 h-4" /> Score Selected</>}
            </button>
            {/* Screen Selected */}
            <button
              data-testid="bulk-screen-selected-btn"
              onClick={async () => {
                if (selected.size === 0) return;
                try {
                  const { data } = await api.post('/candidates/bulk-screen', {
                    candidate_ids: [...selected],
                    job_id: jobId,
                  });
                  addToast(`${data.screened || selected.size} candidate${selected.size !== 1 ? 's' : ''} queued for screening`, 'success');
                  setSelected(new Set());
                  fetchData();
                } catch (err) {
                  addToast(err.response?.data?.detail || 'Screen action failed', 'error');
                }
              }}
              className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm border border-blue-300/30"
            >
              <Play className="w-4 h-4" /> Screen Selected
            </button>
            {/* Delete Selected */}
            <button
              data-testid="bulk-delete-btn"
              onClick={() => setShowBulkDelete(true)}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
            >
              <Trash2 className="w-4 h-4" /> Delete {selected.size}
            </button>
          </div>
        </div>
      )}

      {/* ── TABLE / BOARD ────────────────────────────────────────── */}
      {/* Normalize score fields so JSX never receives raw score objects */}
      {(() => {
        const normalizedCandidates = filtered.map((c) => {
          const rawResume = c.resume_score || c.resumescore || c.resumeScore || null;
          const resumeScoreNumber =
            typeof rawResume === 'object' ? (rawResume?.score ?? null) : (rawResume ?? null);

          const liScoreObj = c.linkedin_score || c.linkedinscore || null;
          const liScoreNumber =
            typeof liScoreObj?.score === 'number'
              ? liScoreObj.score
              : c.relevance_score ?? c.relevancescore ?? null;

          return {
            ...c,
            linkedin_score:      liScoreObj || c.linkedin_score || c.linkedinscore || null,
            linkedinscore:       liScoreObj || c.linkedinscore  || c.linkedin_score || null,
            li_score_display:    liScoreNumber,
            resume_score_display: resumeScoreNumber,
          };
        });

        return viewMode === 'list' ? (
      <CandidateTable
        candidates={normalizedCandidates}
        loading={loading}
        onCandidateClick={(c) => navigate(`/candidates/${c.candidate_id}`)}
        selectedIds={selected}
        onSelectCandidate={toggleSelect}
        onSelectAll={toggleSelectAll}
        allSelected={allFilteredSelected}
        sortField={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        colFilters={colFilters}
        onColFilter={setColFilter}
        uniqueSources={uniqueSources}
        uniqueStatuses={uniqueStatuses}
        showScorePopover={showScorePopover}
        onSetScorePopover={setShowScorePopover}
        onViewProfile={setShowProfileModal}
        onViewScore={setShowScoreModal}
        onSetV4Modal={setV4ScoreModal}
        onScoreCandidate={scoreCandidate}
        scoringId={scoringId}
        onScreen={startScreening}
        onSchedule={setShowScheduleModal}
        onDeleteCandidate={setDeleteConfirm}
        onUpdateCandidates={setCandidates}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        onAddCandidate={() => setShowAddModal(true)}
        onSourceAI={(e) => { setPickerAnchor(e.currentTarget); setShowPlatformPicker(true); }}
        sourcingLoading={sourcingLoading}
        allCandidatesCount={candidates.length}
        onRefreshCandidates={fetchData}
      />
        ) : (
        <CandidateKanbanBoard
          candidates={normalizedCandidates}
          loading={loading}
          onCandidateClick={(c) => navigate(`/candidates/${c.candidate_id}`)}
          onSubmitCandidate={(candidate) => setSubmitModalCandidate(candidate)}
          pipelineCounts={pipelineCounts}
        />
        );
      })()}

      </>
      )}

      {submitModalCandidate && (
        <SubmitCandidateModal
          candidate={submitModalCandidate}
          jobId={jobId || submitModalCandidate.job_id}
          onClose={() => setSubmitModalCandidate(null)}
          onSuccess={() => {
            setSubmitModalCandidate(null);
            fetchData();
          }}
        />
      )}

      {/* ═══ BULK DELETE CONFIRM ═════════════════════════════════════ */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBulkDelete(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="bulk-delete-modal">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 text-center mb-2">Delete {selected.size} Candidates</h3>
            <p className="text-slate-500 text-sm text-center mb-6">
              This will permanently delete <strong>{selected.size} candidate{selected.size !== 1 ? 's' : ''}</strong> from this job. This cannot be undone.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5">
              <div className="max-h-28 overflow-auto space-y-0.5">
                {[...selected].slice(0, 8).map(id => {
                  const c = candidates.find(x => x.candidate_id === id);
                  return c ? (
                    <div key={id} className="flex items-center gap-2 text-xs text-red-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400" />{c.name}
                    </div>
                  ) : null;
                })}
                {selected.size > 8 && <p className="text-xs text-red-500">...and {selected.size - 8} more</p>}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowBulkDelete(false)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm hover:bg-slate-50">Cancel</button>
              <button onClick={bulkDelete} disabled={bulkDeleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {bulkDeleting ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4" /> Delete All</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SINGLE DELETE CONFIRM ══════════════════════════════════ */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 text-center mb-1">Delete Candidate</h3>
            <p className="text-slate-500 text-sm text-center mb-5">
              Remove <strong>{candidates.find(c => c.candidate_id === deleteConfirm)?.name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50">Cancel</button>
              <button onClick={() => deleteCandidate(deleteConfirm)} disabled={deleting === deleteConfirm}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {deleting === deleteConfirm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADD CANDIDATE MODAL ════════════════════════════════════ */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg p-7 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="add-candidate-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Add Candidate Manually</h2>
                <p className="text-xs text-slate-400 mt-0.5">Will appear in list immediately after saving</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={addCandidate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-600 text-xs font-semibold block mb-1">Full Name <span className="text-red-500">*</span></label>
                  <input data-testid="candidate-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none" placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="text-slate-600 text-xs font-semibold block mb-1">Email <span className="text-red-500">*</span></label>
                  <input data-testid="candidate-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none" placeholder="jane@email.com" />
                </div>
              </div>
              <div>
                <label className="text-slate-600 text-xs font-semibold block mb-1">Phone</label>
                <input data-testid="candidate-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none" placeholder="+1 234 567 8900" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-slate-600 text-xs font-semibold">Resume <span className="text-red-500">*</span></label>
                  <div className="flex bg-slate-100 rounded-lg p-0.5">
                    {['text', 'file'].map(m => (
                      <button key={m} type="button" onClick={() => setResumeMode(m)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${resumeMode === m ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                        {m === 'text' ? 'Paste Text' : 'Upload File'}
                      </button>
                    ))}
                  </div>
                </div>
                {resumeMode === 'file' ? (
                  <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-5 text-center cursor-pointer hover:border-[#2563eb]/50 hover:bg-blue-50/20 transition-all" onClick={() => fileRef.current?.click()} data-testid="resume-upload-zone">
                    <input ref={fileRef} type="file" accept={RESUME_ACCEPT} className="hidden" onChange={e => handleResumeUpload(e.target.files[0])} />
                    {extracting ? <><Loader2 className="w-6 h-6 text-[#2563eb] animate-spin mx-auto" /><p className="text-xs text-slate-500 mt-1">Extracting text...</p></> :
                     resumeFile ? <><CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" /><p className="text-xs text-emerald-600 mt-1 font-medium">{resumeFile.name}</p><p className="text-[10px] text-slate-400">Text extracted ✓</p></> :
                     <><Upload className="w-7 h-7 text-slate-400 mx-auto" /><p className="text-sm text-slate-600 font-medium mt-1">Click to upload resume</p><p className="text-[11px] text-slate-400">PDF, DOC, DOCX, TXT</p></>}
                  </div>
                ) : (
                  <textarea data-testid="candidate-resume-input" value={form.resume_text} onChange={e => setForm({...form, resume_text: e.target.value})} rows={6}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none resize-none" placeholder="Paste resume text here..." />
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm hover:bg-slate-50">Cancel</button>
                <button data-testid="save-candidate-btn" type="submit" disabled={saving || !form.resume_text.trim() || !form.name.trim() || !form.email.trim()}
                  className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</> : 'Add Candidate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ PROFILE MODAL ══════════════════════════════════════════ */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowProfileModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div><h2 className="text-lg font-bold text-slate-800">{showProfileModal.name}</h2><p className="text-xs text-slate-400">{showProfileModal.source_type === 'sourced' ? showProfileModal.source_profile_url : showProfileModal.email}</p></div>
              <button onClick={() => setShowProfileModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            {showProfileModal.source_type === 'sourced' && showProfileModal.sourced_data && (
              <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="grid grid-cols-3 gap-3 text-xs mb-2">
                  {[['Title', showProfileModal.sourced_data.current_title], ['Company', showProfileModal.sourced_data.current_company], ['Location', showProfileModal.sourced_data.us_location]].map(([l, v]) => (
                    <div key={l}><p className="text-slate-400 mb-0.5">{l}</p><p className="font-semibold text-slate-700">{v || '—'}</p></div>
                  ))}
                </div>
                {showProfileModal.sourced_data.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {showProfileModal.sourced_data.skills.slice(0, 12).map((s) => <span key={s} className="px-2 py-0.5 bg-white border border-blue-200 rounded-full text-xs text-[#2563eb]">{s}</span>)}
                  </div>
                )}
              </div>
            )}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">{showProfileModal.resume_text || 'No resume text available.'}</pre>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PLATFORM PICKER PORTAL ════════════════════════════════ */}
      {showPlatformPicker && (
        <PlatformPicker
          anchorEl={pickerAnchor}
          onSelect={(platform) => { setShowPlatformPicker(false); setPickerAnchor(null); startSourcing(platform); }}
          onClose={() => { setShowPlatformPicker(false); setPickerAnchor(null); }}
        />
      )}

      {/* ═══ SOURCING MODAL ═════════════════════════════════════════ */}
      {showSourcingModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSourcingModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-xl p-7 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSourceRunning ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                  {isSourceRunning ? <Loader2 className="w-5 h-5 text-[#2563eb] animate-spin" /> : <CheckCircle className="w-5 h-5 text-emerald-600" />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">AI Sourcing</h2>
                  <p className="text-xs text-slate-400">
                    {sourcingSession?._pipeline === 'v4'
                      ? 'Exa → GPT-5.4 Nano Gate → GPT-5.4 Mini Score'
                      : 'GitHub + Exa AI Neural Search · US only'}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowSourcingModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            {sourcingSession && (
              <>
                <div className={`rounded-xl p-4 mb-4 border ${sourcingSession.status === 'completed' ? 'bg-emerald-50 border-emerald-200' : sourcingSession.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-100'}`}>
                  <div className="flex items-center justify-between mb-2">
                    {/* Status label */}
                    <p className={`font-semibold text-sm ${sourcingSession.status === 'completed' ? 'text-emerald-700' : sourcingSession.status === 'error' ? 'text-red-600' : 'text-[#2563eb]'}`}>
                      {sourcingSession._pipeline === 'v4' ? ({
                        started: 'Initializing...',
                        generating_query: 'Generating search query...',
                        searching: 'Searching LinkedIn profiles (Exa)...',
                        gating: `Flash gate: filtering ${sourcingSession.raw_found || '?'} profiles...`,
                        scoring: `o4-mini scoring ${sourcingSession.after_gate || '?'} candidates...`,
                        building_profiles: 'Saving scored candidates...',
                        completed: '✅ Complete!',
                        error: `❌ ${sourcingSession.error || 'Error'}`,
                      }[sourcingSession.status] || 'Processing...')
                      : (sourcingSession.status === 'completed' ? '✅ Complete!' : sourcingSession.status === 'searching' ? 'Searching platforms...' : 'Processing...')}
                    </p>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-800">{sourcingSession.candidates_saved || 0}</p>
                      <p className="text-xs text-slate-400">saved</p>
                    </div>
                  </div>
                  {/* v4 pipeline stage indicators */}
                  {sourcingSession._pipeline === 'v4' && isSourceRunning && (
                    <div className="space-y-1.5 mt-3">
                      {[
                        { key: 'searching', label: 'Exa Deep Search', done: ['gating','scoring','building_profiles','completed'].includes(sourcingSession.status), count: sourcingSession.raw_found },
                        { key: 'gating', label: 'GPT-5.4 Nano Gate', done: ['scoring','building_profiles','completed'].includes(sourcingSession.status), count: sourcingSession.after_gate },
                        { key: 'scoring', label: 'GPT-5.4 Mini Score', done: ['building_profiles','completed'].includes(sourcingSession.status), count: sourcingSession.after_scoring },
                      ].map(step => (
                        <div key={step.key} className="flex items-center gap-2 text-xs">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-emerald-500' : sourcingSession.status === step.key ? 'bg-[#2563eb] animate-pulse' : 'bg-slate-200'}`}>
                            {step.done && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <span className={step.done ? 'text-emerald-700' : sourcingSession.status === step.key ? 'text-[#2563eb] font-semibold' : 'text-slate-400'}>
                            {step.label}{step.count != null ? ` · ${step.count}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Legacy progress bar */}
                  {sourcingSession._pipeline !== 'v4' && isSourceRunning && (
                    <div className="mt-3">
                      <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#2563eb] rounded-full transition-all" style={{ width: `${Math.round(((sourcingSession.platforms_completed || 0) / (sourcingSession.platforms_total || 1)) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  {sourcingSession.status === 'completed' && (
                    <button onClick={() => { setShowSourcingModal(false); fetchData(); setColFilters(p => ({...p, source: 'all'})); }} className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 rounded-xl text-sm font-semibold">
                      View Sourced Candidates
                    </button>
                  )}
                  {isSourceRunning && <button onClick={() => setShowSourcingModal(false)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm hover:bg-slate-50">Run in Background</button>}
                  {!isSourceRunning && (
                    <button onClick={() => { setShowSourcingModal(false); startSourcingV4(); }} disabled={sourcingLoading} className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm border border-[#2563eb] text-[#2563eb] hover:bg-blue-50">
                      <RefreshCw className="w-4 h-4" /> Re-source
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══ CSV MODAL ══════════════════════════════════════════════ */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCsvModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-7 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-800">Import via CSV</h2>
              <button onClick={() => setShowCsvModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <button onClick={downloadCsvTemplate} className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">
                <Download className="w-4 h-4 text-[#2563eb]" /> Download CSV Template
              </button>
              <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#2563eb]/50 transition-all" onClick={() => csvRef.current?.click()}>
                <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={e => setCsvFile(e.target.files[0])} />
                {csvFile ? <><CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" /><p className="text-sm text-emerald-600 mt-1">{csvFile.name}</p></> :
                <><Upload className="w-6 h-6 text-slate-400 mx-auto" /><p className="text-sm text-slate-500 mt-1">Upload CSV</p></>}
              </div>
              {csvResult && <div className={`rounded-xl p-3 border text-sm font-medium ${csvResult.imported > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-600'}`}>{csvResult.imported} imported, {csvResult.failed} failed</div>}
              <div className="flex gap-3">
                <button onClick={() => setShowCsvModal(false)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm hover:bg-slate-50">Close</button>
                <button onClick={importCsv} disabled={!csvFile || csvImporting} className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {csvImporting ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</> : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showScoreModal && <ScoreDetailModal candidate={showScoreModal} onClose={() => setShowScoreModal(null)} />}

      {v4ScoreModal && (
        <V4ScoreReportModal
          candidate={v4ScoreModal.candidate}
          source={v4ScoreModal.source}
          onClose={() => setV4ScoreModal(null)}
        />
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && (
        <ScheduleInterviewModal
          candidate={showScheduleModal}
          jobId={jobId}
          onClose={() => setShowScheduleModal(null)}
          onSuccess={() => { setShowScheduleModal(null); fetchData(); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Sourcing Panel — v4 Smart Sourcing + Campaign + Auto Agent
// ════════════════════════════════════════════════════════════════════════
function SourcingPanel({ jobId, jobTitle }) {
  const { addToast } = useToast();

  // ── v4 single-run state ────────────────────────────────────────────────
  const [v4Loading, setV4Loading] = useState(false);
  const [v4Session, setV4Session] = useState(null);
  const v4PollRef = useRef(null);

  // ── Campaign state ─────────────────────────────────────────────────────
  const [campaign, setCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const campaignPollRef = useRef(null);

  // ── Agent launcher state ───────────────────────────────────────────────
  const [agentName, setAgentName] = useState(jobTitle ? `${jobTitle} Agent` : '');
  const [agentGoal, setAgentGoal] = useState('');
  const [agentMinScore, setAgentMinScore] = useState(60);
  const [agentMaxCandidates, setAgentMaxCandidates] = useState(25);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);
  const pollRef = useRef(null);

  // ── fetch helpers ──────────────────────────────────────────────────────
  const fetchAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/sourcing-agents');
      const jobAgents = (Array.isArray(data) ? data : []).filter(a => a.job_id === jobId);
      setAgents(jobAgents);
    } catch (err) {
      logger.error('Failed to fetch sourcing agents', err);
    } finally {
      setAgentsLoading(false);
    }
  }, [jobId]);

  const fetchV4Latest = useCallback(async () => {
    try {
      const { data } = await api.get(`/sourcing/v4/${jobId}/latest`);
      setV4Session(data);
      return data;
    } catch { return null; }
  }, [jobId]);

  const fetchCampaignLatest = useCallback(async () => {
    try {
      const { data } = await api.get(`/sourcing/v4/${jobId}/campaign/latest`);
      setCampaign(data);
      return data;
    } catch { return null; }
  }, [jobId]);

  const fetchCampaignStatus = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/sourcing/v4/${jobId}/campaign/${cid}/status`);
      setCampaign(data);
      return data;
    } catch { return null; }
  }, [jobId]);

  useEffect(() => {
    fetchAgents();
    fetchV4Latest();
    fetchCampaignLatest();
  }, [fetchAgents, fetchV4Latest, fetchCampaignLatest]);

  // Poll agents
  useEffect(() => {
    const anyActive = agents.some(a => ['running', 'hunting'].includes(a.status));
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (anyActive) { pollRef.current = setInterval(fetchAgents, 5000); }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [agents, fetchAgents]);

  // Poll v4 single session while active
  useEffect(() => {
    const active = v4Session && ['started', 'generating_query', 'searching', 'gating', 'scoring', 'building_profiles'].includes(v4Session.status);
    if (v4PollRef.current) { clearInterval(v4PollRef.current); v4PollRef.current = null; }
    if (active) {
      v4PollRef.current = setInterval(async () => {
        const data = await fetchV4Latest();
        if (data && ['completed', 'error'].includes(data.status)) {
          clearInterval(v4PollRef.current); v4PollRef.current = null;
        }
      }, 5000);
    }
    return () => { if (v4PollRef.current) clearInterval(v4PollRef.current); };
  }, [v4Session?.status, fetchV4Latest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll campaign while running
  useEffect(() => {
    const cid = campaign?.campaign_id;
    const isRunning = campaign?.status === 'running';
    if (campaignPollRef.current) { clearInterval(campaignPollRef.current); campaignPollRef.current = null; }
    if (cid && isRunning) {
      campaignPollRef.current = setInterval(async () => {
        const data = await fetchCampaignStatus(cid);
        if (data && ['completed', 'stopped', 'error'].includes(data.status)) {
          clearInterval(campaignPollRef.current); campaignPollRef.current = null;
          if (data.status === 'completed') {
            addToast(`Campaign complete — ${data.total_saved} candidates sourced across ${data.completed_runs} runs`, 'success');
          }
        }
      }, 6000);
    }
    return () => { if (campaignPollRef.current) clearInterval(campaignPollRef.current); };
  }, [campaign?.campaign_id, campaign?.status, fetchCampaignStatus, addToast]);

  // ── Actions ────────────────────────────────────────────────────────────
  const runV4Sourcing = async () => {
    setV4Loading(true);
    try {
      const { data } = await api.post(`/sourcing/v4/${jobId}/start`, { max_candidates: 10 });
      setV4Session(data);
      addToast('AI sourcing started — searching for 10 candidates.', 'info');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Sourcing failed. Check your Exa API key in Settings.', 'error');
    } finally {
      setV4Loading(false);
    }
  };

  const startCampaign = async () => {
    if (!v4Session?.session_id) return;
    setCampaignLoading(true);
    try {
      const { data } = await api.post(`/sourcing/v4/${jobId}/campaign/start`, {
        first_session_id: v4Session.session_id,
        first_run_saved: v4Session.candidates_saved || 0,
      });
      setCampaign({ ...data, completed_runs: 1, current_run: 2 });
      addToast('Campaign started — 19 more runs will run automatically.', 'success');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to start campaign.', 'error');
    } finally {
      setCampaignLoading(false);
    }
  };

  const stopCampaign = async () => {
    if (!campaign?.campaign_id) return;
    try {
      await api.post(`/sourcing/v4/${jobId}/campaign/${campaign.campaign_id}/stop`);
      setCampaign(prev => ({ ...prev, status: 'stopped' }));
      addToast('Campaign stopped.', 'info');
    } catch (err) {
      addToast('Failed to stop campaign.', 'error');
    }
  };

  const handleLaunchAgent = async (e) => {
    e.preventDefault();
    if (!agentName.trim()) { addToast('Agent name required', 'error'); return; }
    setAgentLoading(true);
    try {
      await api.post('/sourcing-agents', {
        name: agentName.trim(), job_id: jobId,
        goal: agentGoal.trim(), min_score: Number(agentMinScore),
        max_candidates: Number(agentMaxCandidates),
      });
      addToast('Agent launched successfully', 'success');
      setAgentGoal('');
      await fetchAgents();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to launch agent', 'error');
    } finally { setAgentLoading(false); }
  };

  const handleStart = async (agent) => {
    setStartingId(agent.agent_id);
    try {
      await api.post(`/sourcing-agents/${agent.agent_id}/start`);
      addToast(`${agent.name} started`, 'success');
      await fetchAgents();
    } catch { addToast('Failed to start', 'error'); }
    finally { setStartingId(null); }
  };

  const handleStop = async (agent) => {
    setStoppingId(agent.agent_id);
    try {
      await api.post(`/sourcing-agents/${agent.agent_id}/stop`);
      addToast(`${agent.name} stopped`, 'success');
      await fetchAgents();
    } catch { addToast('Failed to stop', 'error'); }
    finally { setStoppingId(null); }
  };

  // ── Derived ────────────────────────────────────────────────────────────
  const v4Active = v4Session && ['started', 'generating_query', 'searching', 'gating', 'scoring', 'building_profiles'].includes(v4Session.status);
  const campaignActive = campaign?.status === 'running';
  // Show "Start 19 more runs?" only when: first run completed, no active campaign, no active campaign just finished showing already
  const showCampaignPrompt = v4Session?.status === 'completed'
    && !v4Active
    && (!campaign || (campaign.status === 'stopped'))
    && !campaignActive;

  const v4StatusLabel = {
    started: 'Starting pipeline…', generating_query: 'Generating AI query…',
    searching: 'Searching LinkedIn via Exa…', gating: 'Running relevance gate…',
    scoring: 'Scoring profiles with AI…', building_profiles: 'Building candidate profiles…',
    completed: 'Sourcing complete', error: 'Sourcing error',
  }[v4Session?.status] || '';

  const campaignPct = campaign
    ? Math.min(100, Math.round((campaign.completed_runs / campaign.total_runs) * 100))
    : 0;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="sourcing-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* ── Smart Sourcing card ─────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="smart-sourcing-card">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">⚡</span>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Smart Sourcing</h3>
              <p className="text-xs text-slate-500">AI query → Exa search → gate → scored candidates</p>
            </div>
          </div>

          {/* Single-run session status */}
          {v4Session && !campaignActive && (
            <div data-testid="v4-session-status"
              className={`mb-3 p-3 rounded-lg text-xs ${
                v4Session.status === 'error' ? 'bg-red-50 text-red-700'
                : v4Session.status === 'completed' ? 'bg-emerald-50 text-emerald-700'
                : 'bg-blue-50 text-blue-700'
              }`}
            >
              <p className="font-semibold mb-1">{v4StatusLabel || v4Session.status}</p>
              {v4Session.status === 'completed' && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  <span>Found: <b>{v4Session.raw_found ?? 0}</b></span>
                  <span>Gate: <b>{v4Session.after_gate ?? 0}</b></span>
                  <span>Saved: <b>{v4Session.candidates_saved ?? 0}</b></span>
                </div>
              )}
              {v4Session.status === 'error' && <p>{v4Session.error || 'An error occurred'}</p>}
              {v4Active && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-3 h-3 border-2 border-blue-400/30 border-t-blue-600 rounded-full animate-spin" />
                  <span>Running…</span>
                </div>
              )}
            </div>
          )}

          {/* Campaign status */}
          {campaign && (
            <div data-testid="campaign-status"
              className={`mb-3 p-3 rounded-lg text-xs ${
                campaign.status === 'completed' ? 'bg-emerald-50 text-emerald-700'
                : campaign.status === 'stopped' ? 'bg-slate-50 text-slate-600'
                : 'bg-indigo-50 text-indigo-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold">
                  {campaign.status === 'completed' ? 'Campaign Complete' 
                   : campaign.status === 'stopped' ? 'Campaign Stopped'
                   : `Campaign — Run ${campaign.current_run || campaign.completed_runs + 1}/${campaign.total_runs}`}
                </p>
                <span className="font-bold">{campaign.total_saved} saved</span>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-indigo-100/60 rounded-full h-2 mb-1">
                <div
                  className={`h-2 rounded-full transition-all duration-700 ${campaign.status === 'completed' ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                  style={{ width: `${campaignPct}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] opacity-70">
                <span>{campaign.completed_runs}/{campaign.total_runs} runs</span>
                <span>{campaign.total_saved}/{campaign.max_candidates} candidates</span>
              </div>
              {campaignActive && (
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                  <span className="text-[10px]">Running run {campaign.current_run}…</span>
                </div>
              )}
            </div>
          )}

          {/* "Start 19 more runs?" prompt */}
          {showCampaignPrompt && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs" data-testid="campaign-prompt">
              <p className="font-semibold text-amber-800 mb-1">
                Run 1 complete — {v4Session.candidates_saved || 0} candidates found
              </p>
              <p className="text-amber-700 mb-2">
                Start 19 more automatic runs to source up to 200 candidates?
              </p>
              <button
                onClick={startCampaign}
                disabled={campaignLoading}
                data-testid="start-campaign-btn"
                className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {campaignLoading
                  ? <><span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Starting…</>
                  : 'Start 19 More Runs →'}
              </button>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={runV4Sourcing}
              disabled={v4Loading || v4Active || campaignActive}
              data-testid="run-v4-sourcing-btn"
              className="flex-1 py-2 bg-[#2563eb] hover:bg-[#1e40af] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {(v4Loading || v4Active) ? (
                <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {v4Active ? 'Sourcing…' : 'Starting…'}</>
              ) : campaignActive ? 'Campaign Running' : 'Run Sourcing →'}
            </button>
            {campaignActive && (
              <button
                onClick={stopCampaign}
                data-testid="stop-campaign-btn"
                className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold rounded-lg border border-red-200 transition-colors"
              >
                Stop
              </button>
            )}
          </div>
        </div>

        {/* ── Auto Agent card ─────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="auto-agent-card">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">🤖</span>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Auto Agent</h3>
              <p className="text-xs text-slate-500">Runs continuously until goal is reached</p>
            </div>
          </div>
          <form onSubmit={handleLaunchAgent} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Agent Name *</label>
              <input data-testid="agent-name" value={agentName} onChange={e => setAgentName(e.target.value)}
                placeholder="e.g. VLSI CAD Hunter" required
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Goal Description (optional)</label>
              <textarea data-testid="agent-goal" value={agentGoal} onChange={e => setAgentGoal(e.target.value)}
                rows={2} placeholder="Describe ideal candidates…"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Min Score</label>
                <input data-testid="agent-min-score" type="number" min={0} max={100} value={agentMinScore} onChange={e => setAgentMinScore(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Candidate Goal</label>
                <input data-testid="agent-max-candidates" type="number" min={1} max={500} value={agentMaxCandidates} onChange={e => setAgentMaxCandidates(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
              </div>
            </div>
            <button type="submit" disabled={agentLoading} data-testid="agent-launch-btn"
              className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {agentLoading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Launching…</> : '🚀 Launch Agent →'}
            </button>
          </form>
        </div>
      </div>

      {/* ── Active Agents ──────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="active-agents-card">
        <h3 className="text-sm font-bold text-slate-800 mb-4">Active Agents on this Job</h3>
        {agentsLoading ? (
          <div className="text-sm text-slate-400 text-center py-6" data-testid="active-agents-loading">Loading…</div>
        ) : agents.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-6" data-testid="active-agents-empty">No agents for this job yet. Launch one above ↑</div>
        ) : (
          <div className="space-y-3">
            {agents.map(agent => {
              const found = agent.progress?.found ?? 0;
              const goal = agent.progress?.goal ?? agent.max_candidates ?? 0;
              const percent = Math.min(100, goal ? Math.round((found / goal) * 100) : 0);
              const active = ['running', 'hunting'].includes(agent.status);
              const statusColor =
                agent.status === 'goal_reached' ? 'bg-emerald-100 text-emerald-700' :
                active ? 'bg-blue-100 text-blue-700' :
                agent.status === 'error' ? 'bg-rose-100 text-rose-700' :
                'bg-slate-100 text-slate-600';
              return (
                <div key={agent.agent_id} data-testid={`job-agent-row-${agent.agent_id}`}
                  className="flex items-center gap-4 p-3 border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-slate-800 truncate">{agent.name}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center ${statusColor}`}>
                        {active && <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mr-1" />}
                        {agent.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${agent.status === 'goal_reached' ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#1e40af] to-[#3b82f6]'}`}
                          style={{ width: `${percent}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{found}/{goal}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {active ? (
                      <button onClick={() => handleStop(agent)} disabled={stoppingId === agent.agent_id}
                        data-testid={`job-agent-stop-${agent.agent_id}`}
                        className="px-3 py-1.5 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg transition-colors disabled:opacity-50">
                        {stoppingId === agent.agent_id ? '…' : 'Stop'}
                      </button>
                    ) : (
                      <button onClick={() => handleStart(agent)} disabled={startingId === agent.agent_id}
                        data-testid={`job-agent-start-${agent.agent_id}`}
                        className="px-3 py-1.5 text-xs font-semibold bg-blue-50 hover:bg-blue-100 text-[#2563eb] rounded-lg transition-colors disabled:opacity-50">
                        {startingId === agent.agent_id ? '…' : 'Start'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Campaign History ──────────────────────────────────────── */}
      <CampaignHistory jobId={jobId} refreshTrigger={campaign?.status} />
    </div>
  );
}


function CampaignHistory({ jobId, refreshTrigger }) {
  const [open, setOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sourcing/v4/${jobId}/campaigns`);
      setCampaigns(Array.isArray(data) ? data : []);
    } catch { setCampaigns([]); }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshTrigger]);

  const statusBadge = (s) => ({
    completed: 'bg-emerald-100 text-emerald-700',
    running:   'bg-blue-100 text-blue-700',
    stopped:   'bg-slate-100 text-slate-600',
    error:     'bg-red-100 text-red-700',
  }[s] || 'bg-slate-100 text-slate-600');

  const stopLabel = (c) => ({
    all_runs_complete:    '20 runs done',
    max_candidates_reached: '200 candidates reached',
    user_stopped:         'Stopped by user',
  }[c.stop_reason] || c.stop_reason || '');

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="campaign-history">
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid="campaign-history-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-800">Campaign History</span>
          {campaigns.length > 0 && !open && (
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{campaigns.length}</span>
          )}
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-slate-100">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : campaigns.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No campaigns run yet for this job.</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {campaigns.map((c, idx) => {
                const isExpanded = expandedId === c.campaign_id;
                const pct = Math.min(100, Math.round((c.completed_runs / c.total_runs) * 100));
                return (
                  <div key={c.campaign_id} data-testid={`campaign-row-${idx}`}>
                    {/* Summary row */}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : c.campaign_id)}
                      className="w-full px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left"
                    >
                      {/* Date + status */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-slate-700">{formatDate(c.started_at)}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadge(c.status)}`}>{c.status}</span>
                          {c.stop_reason && <span className="text-[10px] text-slate-400">{stopLabel(c)}</span>}
                        </div>
                        {/* Progress bar */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-1.5 rounded-full ${c.status === 'completed' ? 'bg-emerald-500' : c.status === 'stopped' ? 'bg-slate-400' : 'bg-blue-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">{c.completed_runs}/{c.total_runs} runs</span>
                        </div>
                      </div>
                      {/* Saved count */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-black text-slate-800">{c.total_saved}</div>
                        <div className="text-[10px] text-slate-400">saved</div>
                      </div>
                      {/* Expand icon */}
                      <svg className={`w-3.5 h-3.5 text-slate-300 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Per-run breakdown */}
                    {isExpanded && c.runs?.length > 0 && (
                      <div className="px-5 pb-4 bg-slate-50/60" data-testid={`campaign-runs-${idx}`}>
                        <div className="grid grid-cols-[40px_1fr_60px_60px_60px] gap-2 px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-1">
                          <span>Run</span><span>Status</span><span className="text-center">Found</span><span className="text-center">Gate</span><span className="text-center">Saved</span>
                        </div>
                        <div className="space-y-0.5 max-h-52 overflow-y-auto">
                          {c.runs.map((run) => (
                            <div key={run.run_num} className="grid grid-cols-[40px_1fr_60px_60px_60px] gap-2 px-2 py-1.5 text-xs items-center rounded hover:bg-white transition-colors">
                              <span className="font-bold text-slate-500">#{run.run_num}</span>
                              <div className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${run.status === 'completed' ? 'bg-emerald-500' : run.status === 'error' ? 'bg-red-400' : 'bg-slate-300'}`} />
                                <span className="text-slate-500 truncate">{run.status}</span>
                              </div>
                              <span className="text-center text-slate-600 font-medium">{run.raw_found ?? '—'}</span>
                              <span className="text-center text-slate-600 font-medium">{run.gate ?? '—'}</span>
                              <span className={`text-center font-bold ${(run.saved || 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{run.saved ?? 0}</span>
                            </div>
                          ))}
                        </div>
                        {/* Campaign totals row */}
                        <div className="grid grid-cols-[40px_1fr_60px_60px_60px] gap-2 px-2 py-2 mt-1 border-t border-slate-200 text-xs font-bold">
                          <span className="text-slate-400">Total</span>
                          <span />
                          <span className="text-center text-slate-700">{c.runs.reduce((s, r) => s + (r.raw_found || 0), 0)}</span>
                          <span className="text-center text-slate-700">{c.runs.reduce((s, r) => s + (r.gate || 0), 0)}</span>
                          <span className="text-center text-emerald-700">{c.total_saved}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
